import userProfile from "user-profile";
import storageManager from "../../../utils/storageManager";
import template from './grid-auto-refresh-component.html';
import ko from "knockout";
import dataModel from "data-model";
const storageKey = 'ge-grid-auto-refresh-state';

export const options = [
    { label: 'Off', value: -1 },
    { label: '30 Seconds', value: 30 },
    { label: '1 Minute', value: 60 },
    { label: '3 Minute', value: 180 },
]

type UpdateType = "INIT_LOAD" | "REFRESH" | "FETCH" | "PRE_FETCH" | "VISIBILITY_CHANGE" | "FETCH_COMPLETE" | "AUTO_REFRESH_STOPPED";

interface IGridAutoRefreshManagerProps {
    gridId: string;
    cacheKey?: string; // must be unique.
    fetchUrl?: string;
    initData?: any[];
    // When true will not fetch data, but instead call `onRefreshTrigger` when time to refresh.
    // This allows manually handing fetching and storing data.
    intervalSeconds?: number; // seconds
    useRefreshTimerOnly?: boolean; 
    fetchParams?: any;
    fetchType?: "GET" | "POST";
    responseRecordsProp?: string;
    responseTotalRecordsProp?: string;
    getFetchParams?: () => any;
    onFetchStatus?: (status: UpdateType) => void;
    onFetchError?: (error: any) => void;
    onFetchComplete?: (response: any) => any | undefined;
    onRefreshTrigger?: () => void;
    onRefresh?: (data: IGridAutoRefreshDataState) => void;
    onRefreshStopCompleted?: () => void;
    onUserInactivity?: () => void;
}

interface IGridAutoRefreshComponentProps {
    showModal: ko.Observable<boolean>;
    gridId: string;
    cacheKey: string; // required | unique
    onClose: (interval: number | undefined) => void;
}

interface IGridAutoRefreshSavedState {
    userId: string;
    gridName: string;
    interval: number;
}

interface IGridAutoRefreshDataState {
    records: any[],
    timestamp: number,
    totalrecords?: number,
}

class GridAutoRefreshSavedState {
    constructor(
        public userId: string,
        public gridName: string,
        public interval: number) {
    }
}

export const fetchGridDataAsync = (fetchUrl: string, fetchType: string, fetchParams: any) => {
    return new Promise<any[]>((resolve, reject) => {
        dataModel.fetchAsync(fetchUrl, fetchType ?? "GET", fetchParams).done((data) => resolve(data))
            .fail((error) => reject(error));
    })
}

const loadSavedOption = async (id: string) => {
    const storedStates = await storageManager.get(storageKey) as IGridAutoRefreshSavedState[] | undefined;

    const match = (storedStates ?? []).find(
        (x) => x.userId === userProfile.userId && x.gridName === id
    );

    return match ?? new GridAutoRefreshSavedState(userProfile.userId, `${id}`, options[0].value);
};

export class GridAutoRefreshManager {
    private readonly props: IGridAutoRefreshManagerProps;
    private dataState: IGridAutoRefreshDataState | undefined = { records: [], totalrecords: 0, timestamp: Date.now() };
    private isFetching = false;
    private fetchTimoutId?: number; // setTimeout
    private refreshTimeoutId?: number; // setTimeou
    private activityTimeoutId?: number;
    private userIsActive = false;
    private fetchIntervalInSeconds: number = -1; // Continuous data fetch interval
    private refreshIntervalInSeconds: number = -1; // Callback trigger interval
    private $container: HTMLElement | undefined;
    private lastRefreshTimestamp;
    private _isRunning: boolean = false;

    constructor(props: IGridAutoRefreshManagerProps) {
        this.props = props;

        if(!this.props.useRefreshTimerOnly && !this.props.cacheKey && !this.props.intervalSeconds) {
            throw new Error("CacheKey is required for auto-refresh when 'intervalSeconds' is not provided.")
        }

        this.setupTimers();
    }
    
    // Setup interval timers
    private setupTimers = async (state?: IGridAutoRefreshSavedState) => {
        // Set initial interval from the saved state
        const savedState = state ?? await this.getSavedIntervalState(this.props.cacheKey);
                        
        const interval = this.props.intervalSeconds ?? savedState.interval ?? -1;
        const adjustedInterval = (interval / 2) > 0 ? (interval / 2) : 0;
        this.fetchIntervalInSeconds = adjustedInterval;
        this.refreshIntervalInSeconds = interval;
    }

    /**
     * Check if timers are running.
     * @returns boolean
     */
    isRunning = () => {
        return this._isRunning;
    }

    /**
     * Updates callback refresh intervals and restarts timers.
     * @param refreshIntervalInSeconds The interval for triggering onRefresh in seconds
     */
    setInterval = (
        refreshIntervalInSeconds: number
    ) => {
        this.refreshIntervalInSeconds = refreshIntervalInSeconds;
        this.fetchIntervalInSeconds = !this.props.useRefreshTimerOnly && (refreshIntervalInSeconds - 10) > 0 ? refreshIntervalInSeconds - 10 : 0;
        this.restartIntervals();
    };

    /**
     * Gets the saved auto-refresh state from local storage
     * @param id
     */
    private getSavedIntervalState = (id: string) => {
        return loadSavedOption(id);
    };

    /**
     * Starts both fetch and callback intervals
     */
    private restartIntervals = () => {
        this.StopFetchTimer();
        this.StopRefreshTimer();

        if(!this.props.useRefreshTimerOnly) {
            this.StartFetchTimer();
        }

        this.StartRefreshTimer();
    };

    // Stop fetching
    private StopFetchTimer = () => {
        if (this.fetchTimoutId) {
            clearTimeout(this.fetchTimoutId);
            this.fetchTimoutId = undefined;
        }
    }

    // Start fetching
    private StartFetchTimer = () => {
        if (this.fetchIntervalInSeconds > 0) {
            this.StopFetchTimer();

            this.fetchTimoutId = window.setTimeout(async () => {
                await this.fetchData("PRE_FETCH");
            }, this.fetchIntervalInSeconds * 1000);
        }
    }

    // Stop refresh
    private StopRefreshTimer = () => {
        if (this.refreshTimeoutId) {
            clearTimeout(this.refreshTimeoutId);
            this.refreshTimeoutId = undefined;

            this._isRunning = false;
        }
    }

    // Start refresh
    private StartRefreshTimer = () => {
        if (this.refreshIntervalInSeconds > 0) {
            this.StopRefreshTimer();

            this.refreshTimeoutId = window.setTimeout(async () => {
                // Trigger callback only if the user is not active
                if (!this.userIsActive) {
                    await this.handleRefresh();
                }
            }, this.refreshIntervalInSeconds * 1000);

            this._isRunning = true;
        }
    }

    /**
     * Handles periodic updates to the parent via the onRefresh callback
     */
    private handleRefresh = async () => {
        await this.waitForFetchCompletion(); // Always wait for fetch to complete
        this.lastRefreshTimestamp = Date.now();

        if(this.props.onRefreshTrigger) {
            this.props.onRefreshTrigger();
        }

        if(this.props.onRefresh) {
            this.props.onRefresh(this.dataState);
        }

        this.StartRefreshTimer();
    };

    /**
     * Wait for ongoing fetch to complete
     */
    private waitForFetchCompletion = (): Promise<void> => {
        return new Promise((resolve) => {
            const intervalId = setInterval(() => {
                if (!this.isFetching) {
                    clearInterval(intervalId);
                    resolve();
                }
            }, 100); // Check every 100ms
        });
    };

    /**
     * Monitors user activity and pauses refresh callbacks
     */
    private windowActivityHandler = () => {
        if (this.activityTimeoutId) {
            clearTimeout(this.activityTimeoutId);
        }

        this.userIsActive = true;

        // Reset to inactive after 1.5 seconds of inactivity
        this.activityTimeoutId = window.setTimeout(() => {
            this.userIsActive = false;
        }, 1500);
    };

    /**
     * Handles browser visibility changes
     */
    private handleVisibilityChange = async () => {
        if (document.visibilityState === "visible") {
            const now = Date.now();
            const timeSinceLastRefresh = now - (this.lastRefreshTimestamp ?? 0);

            if (this._isRunning && timeSinceLastRefresh > 5 * 1000) {
                this.StopFetchTimer();
                await this.fetchData("VISIBILITY_CHANGE");

                // Perform the refresh
                await this.handleRefresh();
            }
        }
    };

    /**
     * Initialize activity tracking on the grid element
     */
    private initActivityTracking = () => {
        this.clearActivityTracking();

        this.$container = $(`#${this.props.gridId}`).closest("div")[0] as HTMLElement;
        if (!this.$container) {
            console.warn("Grid parent container is not found!");
            return;
        }

        // Track activity within the grid container
        const events = ["mousemove", "mousedown", "keydown", "click"];
        events.forEach((event) =>
            this.$container?.addEventListener(event, this.windowActivityHandler)
        );

        document.addEventListener("visibilitychange", this.handleVisibilityChange);
    };

    /**
     * Remove event listeners, etc
     */
    private clearActivityTracking = () => {
        if (this.$container) {
            const events = ["mousemove", "mousedown", "keydown", "click"];
            events.forEach((event) =>
                this.$container?.removeEventListener(event, this.windowActivityHandler)
            );
        }

        document.removeEventListener("visibilitychange", this.handleVisibilityChange);

        if (this.activityTimeoutId) {
            clearTimeout(this.activityTimeoutId);
            this.activityTimeoutId = undefined;
        }

        this.userIsActive = false;
    };

    /**
     * Fetch data from external source
     */
    fetchData = async (updateType = null) => {
        // Check if a fetch is already in progress
        if (this.isFetching) {
            return;
        }

        if(this.props.useRefreshTimerOnly) {
            return;
        }
    
        try {
            this.isFetching = true;

            if (this.props.onFetchStatus) {
                this.props.onFetchStatus(updateType ?? "PRE_FETCH");
            }

            const params = this.props?.fetchParams ?? this.props?.getFetchParams() ?? {};
    
            let data = await fetchGridDataAsync(this.props.fetchUrl, this.props.fetchType ?? "GET", params);
    
            if (this.props.onFetchComplete) {
                data = this.props.onFetchComplete(data);
            }
    
            if (data) {
                this.updateDataState(data);
            }

        } catch (err) {
            console.error(err);
    
            if (this.props.onFetchError) {
                this.props.onFetchError(err);
            }
        } finally {
            this.isFetching = false; // Reset fetch flag
            this.StartFetchTimer();

            if (this.props.onFetchStatus) {
                this.props.onFetchStatus("FETCH_COMPLETE");
            }
        }

        return this.dataState;
    };

    /**
     * Helper function to update data state
     * @param response 
     * @param paging 
     */
    private updateDataState = (response: any) => {
        let _data = [];

        if (this.props.responseRecordsProp) {
            _data = response[this.props.responseRecordsProp] ?? [];
        } else {
            _data = response ?? [];
        }

        this.dataState.records = _data;
        this.dataState.totalrecords = this.props.responseTotalRecordsProp ? (response[this.props.responseTotalRecordsProp] ?? this.dataState.records.length) : this.dataState.records.length;
    };

    /**
     * Set the data manually
     * @param records 
     * @param totalrecords 
     */
    setData = (records: any[], totalrecords?: number) => {
        this.dataState.records = records;
        this.dataState.totalrecords = totalrecords ?? records.length;
        this.dataState.timestamp = Date.now();
    }

    /**
     * Get the current data state.
     * @returns IGridAutoRefreshDataState
     */
    getDataState = () => {
        return this.dataState;
    }

    /**
     * Clear the data state
     */
    clearData = () => {
        this.dataState.records = [];
        this.dataState.totalrecords = 0;
        this.dataState.timestamp = 0;
    }

    /**
     * Refresh data manually
     */
    refresh = async () => {
        this.clearData();
        this.isFetching = false;

        this.initActivityTracking();

        await this.setupTimers();

        this.StopFetchTimer();
        this.StopRefreshTimer();

        await this.fetchData("REFRESH");
        await this.handleRefresh();

        this.restartIntervals();
    }


    /**
     * Restart
     */
    restart = async  () => {
        await this.stop();
        await this.start();
    }

    /**
     * Starts the refresh process
     */
    start = async () => {
        this.stop();
        this.initActivityTracking();
        
        await this.setupTimers();

        if(!this.props.useRefreshTimerOnly) {
            await this.fetchData("INIT_LOAD");
            await this.handleRefresh();
        }
        else {
            this.restartIntervals();
        }
    };

    /**
     * Stops all timers and cleans up user activity tracking
     */
    stop = () => {
        this.isFetching = false;
        this._isRunning = false;

        this.clearActivityTracking();
        this.StopFetchTimer();
        this.StartRefreshTimer();

        if (this.props.onRefreshStopCompleted) {
            this.props.onRefreshStopCompleted();
        }
    };

    dispose = () => {
        this.stop();
    };
}

class GridAutoRefreshComponentViewModel {
    private readonly props: IGridAutoRefreshComponentProps;
    private savedState: IGridAutoRefreshSavedState | undefined;
    private $modal: JQuery<HTMLElement> = $('#grid-auto-refresh-modal');
    private $jqxRefreshOptions: jqwidgets.jqxListBox | undefined;
    private cacheKey = "";

    constructor(props: IGridAutoRefreshComponentProps) {
        this.props = props;
        this.cacheKey = `${this.props.cacheKey}`

        // Initialize UI for refresh interval options
        this.$jqxRefreshOptions = jqwidgets.createInstance("#refresh-interval-options", 'jqxListBox', {
            source: options,
            selectedIndex: 0,
            width: '100%',
            height: 200,
            autoHeight: true,
            displayMember: 'label',
            valueMember: 'value',
        });

        // Load saved settings
        this.handleLoadSavedOption();

        // Bind event handlers
        this.$jqxRefreshOptions.addEventHandler('select', this.handleOptionSelected);
        this.props.showModal.subscribe((show) => {
            if (show) {
                this.$modal.modal('show');
            } else {
                this.$modal.modal('hide');
            }
        });
        this.$modal.on('hidden.bs.modal', () => this.handleClose());
    }

    private handleOptionSelected = () => {
        const option = this.$jqxRefreshOptions?.getSelectedItem();
        if (option) {
            this.savedState = new GridAutoRefreshSavedState(userProfile.userId, this.cacheKey, option.value);
        }
    }

    private handleClose = () => {
        // Save the selected refresh interval
        if (this.savedState) {
            this.saveOption(this.savedState);
        }
        this.props.onClose(this.savedState.interval); // Notify the parent that the modal has closed
    }

    private handleLoadSavedOption = async () => {
        this.savedState = await loadSavedOption(this.cacheKey);

        if(this.savedState) {
            this.$jqxRefreshOptions.selectIndex(options.findIndex(x => x.value === this.savedState?.interval))
        }
    };

    private saveOption = async (state: IGridAutoRefreshSavedState) => {
        const storedStates: IGridAutoRefreshSavedState[]  = await storageManager.get(storageKey) ?? [];
        
        const index = (storedStates ?? []).findIndex(
            (x) => x.userId === state.userId && x.gridName === state.gridName
        );
        if (index > -1) {
            storedStates[index] = state;
        } else {
            storedStates.push(state);
        }

        await storageManager.set(storageKey, storedStates);
    };

    dispose = () => {
        this.$jqxRefreshOptions?.removeEventHandler('select');
    };
}

export default { viewModel: GridAutoRefreshComponentViewModel, template: template }